import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useTheme } from "../../context/ThemeContext";
import { usePatient } from "../../context/PatientContext";
import TransliteratedInput from "../common/TransliteratedInput";
import AutoSave from "../common/AutoSave";
import axios from "axios";
import { Plus, Trash2, Pill, Droplet, Wind, ClipboardList, Printer, Download, Search, ChevronDown, Check } from "lucide-react";
import { motion } from "framer-motion";
import CONFIG from "../../config";

const SYRUP_UNITS = ["ml", "L", "Chamcha"];
const TABLET_UNITS = ["N"];
const POWDER_UNITS = ["Chamcha", "g", "mg"];

const SearchableSelect = ({
  options,
  value,
  onChange,
  onCustomSelect,
  placeholder,
  loading,
  theme,
  isDarkMode,
  disabled,
  allowCustom = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.id.toString() === value?.toString());

  const filteredOptions = options.filter(opt =>
    opt.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 rounded-lg border flex items-center justify-between cursor-pointer transition-all ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        style={{
          backgroundColor: theme.inputBg,
          borderColor: isOpen ? theme.primary : theme.border,
          color: theme.text
        }}
      >
        <span className={`truncate ${!selectedOption && !value ? 'opacity-50' : ''}`}>
          {selectedOption ? selectedOption.name : (value && isNaN(Number(value)) ? value : (loading ? "Loading..." : placeholder))}
        </span>
        <ChevronDown className={`w-4 h-4 min-w-[16px] transition-transform ${isOpen ? 'rotate-180' : ''}`} style={{ color: theme.textSecondary }} />
      </div>

      {isOpen && (
        <div
          className="absolute z-[10000] w-full mt-1 rounded-xl border shadow-2xl overflow-hidden animate-scaleIn"
          style={{ backgroundColor: theme.card, borderColor: theme.border, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)' }}
        >
          <div className="p-2 border-b" style={{ borderColor: theme.border }}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" style={{ color: theme.text }} />
              <TransliteratedInput
                value={searchTerm}
                onChangeText={(text) => setSearchTerm(text)}
                placeholder="Search or Type Custom..."
                className="w-full pl-9 pr-3 py-1.5 rounded-lg border text-sm focus:outline-none"
                style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (filteredOptions.length > 0) {
                      onChange(filteredOptions[0].id.toString());
                      setIsOpen(false);
                      setSearchTerm("");
                    } else if (allowCustom && searchTerm.trim()) {
                      onCustomSelect && onCustomSelect(searchTerm.trim());
                      setIsOpen(false);
                      setSearchTerm("");
                    }
                  }
                  if (e.key === 'Escape') {
                    setIsOpen(false);
                  }
                }}
              />
            </div>
          </div>
          <ul className="max-h-60 overflow-y-auto py-1">
            {filteredOptions.length === 0 ? (
              <li className="px-4 py-3 text-sm text-center opacity-50" style={{ color: theme.text }}>
                {allowCustom && searchTerm ? "Press Enter to add custom" : "No results found"}
              </li>
            ) : (
              filteredOptions.map((opt) => (
                <li
                  key={opt.id}
                  onClick={() => {
                    onChange(opt.id.toString());
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                  className="px-4 py-2.5 text-sm cursor-pointer flex items-center justify-between transition-colors"
                  style={{
                    color: theme.text,
                    backgroundColor: value?.toString() === opt.id.toString() ? `${theme.primary}15` : 'transparent'
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.backgroundColor = `${theme.primary}20`)}
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = value?.toString() === opt.id.toString() ? `${theme.primary}15` : 'transparent')}
                >
                  <span>{opt.name}</span>
                  {value?.toString() === opt.id.toString() && <Check className="w-4 h-4" style={{ color: theme.primary }} />}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const PatientTreatmentForm = (props) => {
  const { theme, isDarkMode } = useTheme();
  const { doctorInfo } = usePatient();
  const { initialValues, onSubmit, onBack, isFirstStep, isLastStep, onAutoSave } = props;

  const patientTreatmentInfo = initialValues.patientTreatment?.[0] || {};
  const patientDiagnosisInfo = initialValues.patientDiagnosis?.[0] || {};

  const [prescriptionItems, setPrescriptionItems] = useState(patientDiagnosisInfo?.PRESCRIPTION ? JSON.parse(patientDiagnosisInfo.PRESCRIPTION) : []);
  const [medicines, setMedicines] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loadingMedicines, setLoadingMedicines] = useState(false);
  const [loadingIngredients, setLoadingIngredients] = useState(false);

  const [currentItem, setCurrentItem] = useState({
    type: "Tablet",
    medicineId: "",
    medicineName: "",
    dosageMorning: "",
    dosageAfternoon: "",
    dosageEvening: "",
    unit: "N",
    days: "",
    ingredients: [{ ingredientId: "", name: "", quantity: "", unit: "g" }]
  });

  useEffect(() => {
    if (currentItem.type === "Powder") {
      fetchIngredients();
    } else {
      fetchMedicines(currentItem.type);
    }
  }, [currentItem.type]);

  const fetchMedicines = async (type) => {
    setLoadingMedicines(true);
    try {
      const response = await axios.get(`${CONFIG.BASE_URL}/medicines`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          doctorId: doctorInfo.MANDALI_ID,
          clinicId: doctorInfo.CLINIC_ID,
          roleId: doctorInfo.MANDALI_ROLE
        }
      });
      setMedicines(response.data.medicines || []);
    } catch (error) {
      console.error("Error fetching medicines:", error);
    } finally {
      setLoadingMedicines(false);
    }
  };

  const fetchIngredients = async () => {
    setLoadingIngredients(true);
    try {
      const response = await axios.get(`${CONFIG.BASE_URL}/ingredients?limit=500`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`
        }
      });
      setIngredients(response.data || []);
    } catch (error) {
      console.error("Error fetching ingredients:", error);
    } finally {
      setLoadingIngredients(false);
    }
  };

  const handleAddItem = () => {
    if (currentItem.type !== "Powder" && !currentItem.medicineId && !currentItem.medicineName) {
      alert("Please select or enter a medicine");
      return;
    }

    if (currentItem.type === "Powder") {
      const hasEmptyIngredient = currentItem.ingredients.some(ing => (!ing.ingredientId && !ing.name) || !ing.quantity);
      if (hasEmptyIngredient) {
        alert("Please select/enter all ingredients and their quantities");
        return;
      }
      if (!currentItem.medicineName) {
        alert("Please enter a name for the custom powder");
        return;
      }
    }

    if (!currentItem.dosageMorning && !currentItem.dosageAfternoon && !currentItem.dosageEvening) {
      alert("Please enter at least one dosage value (Morning, Afternoon, or Evening)");
      return;
    }

    let newItem;
    if (currentItem.type === "Powder") {
      newItem = {
        ...currentItem,
        id: Date.now()
      };
    } else {
      const selectedMed = medicines.find(m => m.id === parseInt(currentItem.medicineId));
      newItem = {
        ...currentItem,
        medicineName: selectedMed ? selectedMed.name : currentItem.medicineName, // Use custom name if no match
        id: Date.now()
      };
    }

    setPrescriptionItems([...prescriptionItems, newItem]);
    setCurrentItem({
      ...currentItem,
      medicineId: "",
      medicineName: "",
      dosageMorning: "",
      dosageAfternoon: "",
      dosageEvening: "",
      days: "",
      ingredients: [{ ingredientId: "", name: "", quantity: "", unit: "g" }]
    });
  };

  const addIngredientField = () => {
    setCurrentItem({
      ...currentItem,
      ingredients: [...currentItem.ingredients, { ingredientId: "", name: "", quantity: "", unit: "g" }]
    });
  };

  const removeIngredientField = (index) => {
    const newIngredients = currentItem.ingredients.filter((_, i) => i !== index);
    setCurrentItem({
      ...currentItem,
      ingredients: newIngredients
    });
  };

  const updateIngredient = (index, field, value) => {
    const newIngredients = [...currentItem.ingredients];
    if (field === "ingredientId") {
      const selectedIng = ingredients.find(ing => ing.id === parseInt(value));
      newIngredients[index] = {
        ...newIngredients[index],
        ingredientId: value,
        name: selectedIng ? selectedIng.name : "",
        unit: "g"
      };
    } else if (field === "customName") {
      // Helper to set custom name when ID is cleared/not present
      newIngredients[index] = {
        ...newIngredients[index],
        ingredientId: "",
        name: value
      };
    } else {
      newIngredients[index] = {
        ...newIngredients[index],
        [field]: value
      };
    }
    setCurrentItem({
      ...currentItem,
      ingredients: newIngredients
    });
  };

  const handleDownload = (treatmentDetails) => {
    if (prescriptionItems.length === 0) return;

    const printWindow = window.open("", "_blank");
    const date = new Date().toLocaleDateString("en-IN");
    const patientName = initialValues?.patientInfo?.[0]?.PATIENT_NAME || "Patient";

    printWindow.document.write(`
      <html>
        <head>
          <title>Prescription - ${patientName}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 30px; color: #333; }
            .presc-container { max-width: 800px; margin: auto; border: 1px solid #eee; padding: 40px; border-radius: 8px; }
            .header { text-align: center; border-bottom: 2px solid #596e62; padding-bottom: 20px; margin-bottom: 30px; }
            .hospital-name { font-size: 28px; font-weight: bold; color: #596e62; margin: 0; }
            .sub-header { font-size: 14px; color: #666; margin-top: 5px; }
            .patient-info { display: flex; justify-content: space-between; margin-bottom: 30px; font-size: 14px; background: #f9f9f9; padding: 15px; border-radius: 8px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { border-bottom: 2px solid #eee; text-align: left; padding: 12px; font-size: 13px; text-transform: uppercase; color: #555; }
            td { padding: 15px 12px; border-bottom: 1px solid #f0f0f0; font-size: 14px; vertical-align: middle; }
            .med-name { font-weight: bold; font-size: 15px; color: #1a1a1a; }
            .rx-symbol { font-size: 24px; font-weight: bold; color: #596e62; margin-bottom: 10px; }
            .notes-section { margin-top: 30px; padding: 15px; background: #fdfdfd; border: 1px dashed #eee; border-radius: 8px; }
            .notes-title { font-size: 12px; font-weight: bold; text-transform: uppercase; color: #596e62; margin-bottom: 8px; border-bottom: 1px solid #f0f0f0; padding-bottom: 4px; display: inline-block; }
            .notes-content { font-size: 14px; line-height: 1.6; white-space: pre-wrap; color: #444; }
            .footer { margin-top: 60px; display: flex; justify-content: space-between; align-items: flex-end; }
            .signature { border-top: 1px solid #333; width: 200px; text-align: center; padding-top: 5px; font-size: 12px; }
            .no-print { display: none; }
            @media print { .print-btn { display: none; } }
            .print-btn { position: fixed; bottom: 20px; right: 20px; padding: 12px 24px; background: #596e62; color: white; border: none; border-radius: 50px; font-weight: bold; cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,0.2); }
          </style>
        </head>
        <body>
          <div class="presc-container">
            <div class="header">
              <h1 class="hospital-name">Amrutdhara Wellness</h1>
              <div class="sub-header">Panchkarma & Ayurvedic Clinic</div>
            </div>

            <div class="patient-info">
              <span>Patient: <b>${patientName}</b></span>
              <span>Date: <b>${date}</b></span>
            </div>

            <table>
              <thead>
                <tr>
                   <th style="width: 35%;">Medicine Name</th>
                   <th>Dosage (M-A-E)</th>
                   <th style="text-align: center;">Duration</th>
                   <th style="text-align: right;">Total Qty</th>
                 </tr>
               </thead>
               <tbody>
                 ${prescriptionItems.map(item => {
      const totalDaily = (Number(item.dosageMorning) || 0) + (Number(item.dosageAfternoon) || 0) + (Number(item.dosageEvening) || 0);
      const totalQty = totalDaily * (Number(item.days) || 0);
      return `
                   <tr>
                     <td>
                       <div class="med-name">${item.medicineName}</div>
                       <div style="font-size: 11px; color: #777; margin-top: 2px;">${item.type}</div>
                     </td>
                     <td>
                       <div>${item.dosageMorning || 0} - ${item.dosageAfternoon || 0} - ${item.dosageEvening || 0}</div>
                       <div style="font-size: 11px; color: #888;">${item.unit} per dose</div>
                     </td>
                     <td style="text-align: center;">
                       <b>${item.days || "—"}</b> Days
                     </td>
                     <td style="text-align: right;">
                       <b>${totalQty} ${item.unit}</b>
                     </td>
                   </tr>
                 `}).join('')}
               </tbody>
            </table>

            ${treatmentDetails ? `
              <div class="notes-section">
                <div class="notes-title">Instructions / Notes</div>
                <div class="notes-content">${treatmentDetails}</div>
              </div>
            ` : ''}

            <div class="footer">
              <div style="font-size: 10px; color: #999;">Generated by Amrutdhara Digital System</div>
              <div class="signature">Authorized Signature</div>
            </div>
          </div>
          <button class="print-btn" onclick="window.print()">Print Prescription</button>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const removeItem = (id) => {
    setPrescriptionItems(prescriptionItems.filter(item => item.id !== id));
  };

  const getUnits = (type) => {
    switch (type) {
      case "Syrup": return SYRUP_UNITS;
      case "Powder": return POWDER_UNITS;
      case "Tablet": return TABLET_UNITS;
      default: return TABLET_UNITS;
    }
  };

  const formInitialValues = {
    TREATMENT_DETAILS: patientTreatmentInfo?.TREATMENT_DETAILS || "",
    DIVAS: patientTreatmentInfo?.DIVAS || "",
  };

  const validationSchema = Yup.object().shape({
    TREATMENT_DETAILS: Yup.string(),
    DIVAS: Yup.number().integer().nullable(true),
  });

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        const finalValues = {
          ...values,
          PRESCRIPTION: JSON.stringify(prescriptionItems)
        };
        onSubmit(finalValues);
        setSubmitting(false);
      }}
      enableReinitialize={true}
    >
      {({ errors, touched, isSubmitting, values }) => (
        <Form className="space-y-6 md:space-y-8 w-full">
          <AutoSave
            onAutoSave={(values) =>
              (onAutoSave || onSubmit)?.(
                {
                  ...values,
                  PRESCRIPTION: JSON.stringify(prescriptionItems)
                },
                { isAutoSave: true }
              )
            }
          />
          {/* Prescription Section moved from Diagnosis */}
          <div className="p-6 rounded-2xl border transition-all duration-300 relative z-[10001]"
            style={{
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              borderColor: theme.border
            }}>
            <div className="flex items-center gap-2 mb-6">
              <ClipboardList className="w-5 h-5" style={{ color: theme.primary }} />
              <h3 className="text-lg font-bold" style={{ color: theme.text }}>Add Prescription</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
              {/* Type Dropdown */}
              <div className="md:col-span-2 space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider opacity-70" style={{ color: theme.text }}>Type</label>
                <select
                  value={currentItem.type}
                  onChange={(e) => {
                    const type = e.target.value;
                    setCurrentItem({
                      ...currentItem,
                      type: type,
                      unit: getUnits(type)[0],
                      medicineId: ""
                    });
                  }}
                  className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                  style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                >
                  <option value="Tablet">Tablet</option>
                  <option value="Powder">Powder</option>
                  <option value="Syrup">Syrup</option>
                </select>
              </div>

              {/* Medicine Dropdown or Custom Powder Name */}
              <div className="md:col-span-3 space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider opacity-70" style={{ color: theme.text }}>
                  {currentItem.type === "Powder" ? "Custom Powder Name" : "Select Medicine"}
                </label>
                {currentItem.type === "Powder" ? (
                  <TransliteratedInput
                    value={currentItem.medicineName}
                    onChangeText={(text) => setCurrentItem({ ...currentItem, medicineName: text })}
                    placeholder="Enter Custom Powder Name"
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                    style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                  />
                ) : (
                  <SearchableSelect
                    options={medicines}
                    value={currentItem.medicineId || currentItem.medicineName}
                    onChange={(val) => setCurrentItem({ ...currentItem, medicineId: val })}
                    allowCustom={true}
                    onCustomSelect={(text) => setCurrentItem({ ...currentItem, medicineId: "", medicineName: text })}
                    placeholder="Select or Type Medicine"
                    loading={loadingMedicines}
                    theme={theme}
                    isDarkMode={isDarkMode}
                  />
                )}
              </div>

              {/* Dosage Inputs */}
              <div className="md:col-span-3 grid grid-cols-3 gap-2">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold uppercase tracking-wider opacity-70" style={{ color: theme.text }}>Morn</label>
                  <input
                    type="text"
                    placeholder="M"
                    value={currentItem.dosageMorning}
                    onChange={(e) => setCurrentItem({ ...currentItem, dosageMorning: e.target.value })}
                    className="w-full px-2 py-2 rounded-lg border text-center focus:outline-none focus:ring-2 transition-all"
                    style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold uppercase tracking-wider opacity-70" style={{ color: theme.text }}>Aftr</label>
                  <input
                    type="text"
                    placeholder="A"
                    value={currentItem.dosageAfternoon}
                    onChange={(e) => setCurrentItem({ ...currentItem, dosageAfternoon: e.target.value })}
                    className="w-full px-2 py-2 rounded-lg border text-center focus:outline-none focus:ring-2 transition-all"
                    style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold uppercase tracking-wider opacity-70" style={{ color: theme.text }}>Even</label>
                  <input
                    type="text"
                    placeholder="E"
                    value={currentItem.dosageEvening}
                    onChange={(e) => setCurrentItem({ ...currentItem, dosageEvening: e.target.value })}
                    className="w-full px-2 py-2 rounded-lg border text-center focus:outline-none focus:ring-2 transition-all"
                    style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                  />
                </div>
              </div>

              {/* Unit Dropdown */}
              <div className="md:col-span-1.5 space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider opacity-70" style={{ color: theme.text }}>Unit</label>
                <select
                  value={currentItem.unit}
                  onChange={(e) => setCurrentItem({ ...currentItem, unit: e.target.value })}
                  className="w-full px-2 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                  style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                >
                  {getUnits(currentItem.type).map(u => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>

              {/* Days Input */}
              <div className="md:col-span-1 space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider opacity-70" style={{ color: theme.text }}>Days</label>
                <input
                  type="number"
                  placeholder="Days"
                  value={currentItem.days}
                  onChange={(e) => setCurrentItem({ ...currentItem, days: e.target.value })}
                  className="w-full px-2 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                  style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                />
              </div>

              {/* Add Button */}
              <div className="md:col-span-1.5">
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98] shadow-md"
                  style={{ backgroundImage: `linear-gradient(to right, ${theme.primary}, ${theme.secondary})` }}
                >
                  <Plus className="w-4 h-4" /> Add
                </button>
              </div>
            </div>

            {currentItem.type === "Powder" && (
              <div className="mt-6 p-4 rounded-xl border animate-fadeIn" style={{ borderColor: theme.border, backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }}>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-bold flex items-center gap-2" style={{ color: theme.text }}>
                    <Wind className="w-4 h-4" /> Ingredients for Powder
                  </h4>
                  <button
                    type="button"
                    onClick={addIngredientField}
                    className="flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-md transition-all hover:scale-105"
                    style={{ backgroundColor: `${theme.primary}20`, color: theme.primary }}
                  >
                    <Plus className="w-3 h-3" /> Add Ingredient
                  </button>
                </div>

                <div className="space-y-3">
                  {currentItem.ingredients.map((ing, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                      <div className="md:col-span-5 space-y-1">
                        <label className="text-[10px] font-semibold uppercase opacity-60" style={{ color: theme.text }}>Select Ingredient</label>
                        <SearchableSelect
                          options={ingredients}
                          value={ing.ingredientId || ing.name}
                          onChange={(val) => updateIngredient(index, "ingredientId", val)}
                          allowCustom={true}
                          onCustomSelect={(text) => updateIngredient(index, "customName", text)}
                          placeholder="Select or Type Ingredient"
                          loading={loadingIngredients}
                          theme={theme}
                          isDarkMode={isDarkMode}
                        />
                      </div>
                      <div className="md:col-span-4 space-y-1">
                        <label className="text-[10px] font-semibold uppercase opacity-60" style={{ color: theme.text }}>Quantity</label>
                        <input
                          type="text"
                          placeholder="Qty"
                          value={ing.quantity}
                          onChange={(e) => updateIngredient(index, "quantity", e.target.value)}
                          className="w-full px-3 py-1.5 rounded-lg border text-sm focus:outline-none"
                          style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                        />
                      </div>
                      <div className="md:col-span-2 space-y-1">
                        <label className="text-[10px] font-semibold uppercase opacity-60" style={{ color: theme.text }}>Unit</label>
                        <select
                          value={ing.unit || "g"}
                          onChange={(e) => updateIngredient(index, "unit", e.target.value)}
                          className="w-full px-3 py-1.5 rounded-lg border text-sm focus:outline-none"
                          style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                        >
                          {POWDER_UNITS.map(u => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-1">
                        {currentItem.ingredients.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeIngredientField(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {prescriptionItems.length > 0 && (
              <div className="mt-8 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold opacity-80" style={{ color: theme.text }}>Added Medicines:</p>
                  <button
                    type="button"
                    onClick={() => handleDownload(values.TREATMENT_DETAILS)}
                    className="flex items-center gap-2 px-4 py-1.5 rounded-lg font-bold text-xs transition-all hover:scale-105 active:scale-95 shadow-md"
                    style={{ backgroundColor: theme.primary, color: 'white' }}
                  >
                    <Download size={14} /> Download Prescription
                  </button>
                </div>
                <div className="overflow-x-auto rounded-[1.5rem] border shadow-md" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
                  <table className="w-full text-left border-collapse" style={{ backgroundColor: theme.inputBg }}>
                    <thead>
                      <tr>
                        <th className="p-4 text-[11px] font-bold uppercase tracking-[0.15em]" style={{ backgroundColor: isDarkMode ? "#243020" : "#f8f9fa", color: theme.text, border: 'none', borderBottom: `2px solid ${theme.border}` }}>Medicine</th>
                        <th className="p-4 text-[11px] font-bold uppercase tracking-[0.15em]" style={{ backgroundColor: isDarkMode ? "#243020" : "#f8f9fa", color: theme.text, border: 'none', borderBottom: `2px solid ${theme.border}` }}>Type</th>
                        <th className="p-4 text-[11px] font-bold uppercase tracking-[0.15em] text-center" style={{ backgroundColor: isDarkMode ? "#243020" : "#f8f9fa", color: theme.text, border: 'none', borderBottom: `2px solid ${theme.border}` }}>Dosage (M-A-E)</th>
                        <th className="p-4 text-[11px] font-bold uppercase tracking-[0.15em] text-center" style={{ backgroundColor: isDarkMode ? "#243020" : "#f8f9fa", color: theme.text, border: 'none', borderBottom: `2px solid ${theme.border}` }}>Qty/Unit</th>
                        <th className="p-4 text-[11px] font-bold uppercase tracking-[0.15em] text-center" style={{ backgroundColor: isDarkMode ? "#243020" : "#f8f9fa", color: theme.text, border: 'none', borderBottom: `2px solid ${theme.border}` }}>Days</th>
                        <th className="p-4 text-[11px] font-bold uppercase tracking-[0.15em] text-center" style={{ backgroundColor: isDarkMode ? "#243020" : "#f8f9fa", color: theme.text, border: 'none', borderBottom: `2px solid ${theme.border}` }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prescriptionItems.map((item, idx) => (
                        <motion.tr
                          key={item.id}
                          className="transition-colors"
                          style={{
                            borderBottom: `1px solid ${theme.border}`,
                            backgroundColor: isDarkMode ? theme.inputBg : 'inherit'
                          }}
                          whileHover={{
                            backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"
                          }}
                        >
                          <td className="p-4 py-5" style={{ border: 'none' }}>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold border"
                                style={{ backgroundColor: theme.primary + '15', color: theme.primary, borderColor: theme.primary + '20' }}>
                                {item.medicineName?.charAt(0) || "M"}
                              </div>
                              <div className="flex flex-col">
                                <span className="font-bold text-[15px]" style={{ color: theme.text }}>{item.medicineName}</span>
                                {item.type === "Powder" && item.ingredients && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {item.ingredients.map((ing, idx) => (
                                      <span key={idx} className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider"
                                        style={{
                                          backgroundColor: `${theme.primary}15`,
                                          color: theme.primary,
                                          border: `1px solid ${theme.primary}30`
                                        }}>
                                        {ing.name}: {ing.quantity} {ing.unit}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-4" style={{ border: 'none' }}>
                            <span className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-sm"
                              style={{
                                backgroundColor: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
                                color: theme.text,
                                border: `1px solid ${theme.border}`
                              }}>
                              {item.type}
                            </span>
                          </td>
                          <td className="p-4 text-center" style={{ border: 'none' }}>
                            <span className="font-mono text-sm px-3 py-1 rounded-lg"
                              style={{
                                backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                                color: theme.text,
                                border: `1px solid ${theme.border}`
                              }}>
                              {item.dosageMorning || 0} - {item.dosageAfternoon || 0} - {item.dosageEvening || 0}
                            </span>
                          </td>
                          <td className="p-4 text-center" style={{ border: 'none' }}>
                            <span className="font-semibold text-sm" style={{ color: theme.textSecondary }}>{item.unit}</span>
                          </td>
                          <td className="p-4 text-center" style={{ border: 'none' }}>
                            <div className="inline-block px-3 py-1 rounded-lg" style={{ backgroundColor: theme.primary + '10' }}>
                              <span className="font-bold text-sm" style={{ color: theme.primary }}>{item.days || "—"}</span>
                            </div>
                          </td>
                          <td className="p-4 text-center" style={{ border: 'none' }}>
                            <button
                              type="button"
                              onClick={() => removeItem(item.id)}
                              className="p-2 rounded-xl transition-all hover:scale-110 active:scale-95 border group"
                              style={{
                                color: theme.danger,
                                borderColor: theme.danger + '30',
                                backgroundColor: theme.danger + '10'
                              }}
                              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = `${theme.danger}20`)}
                              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = `${theme.danger}10`)}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="TREATMENT_DETAILS"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                Treatment Details
              </label>
              <Field name="TREATMENT_DETAILS">
                {({ field, form }) => (
                  <TransliteratedInput
                    {...field}
                    onChangeText={(text) => form.setFieldValue(field.name, text)}
                    className="w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    style={{
                      backgroundColor: theme.inputBg,
                      borderColor: theme.border,
                      color: theme.text,
                    }}
                    as="textarea"
                    rows={8}
                    placeholder="Enter treatment details..."
                  />
                )}
              </Field>
              <ErrorMessage name="TREATMENT_DETAILS" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="DIVAS"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                Days (Optional)
              </label>
              <Field
                name="DIVAS"
                type="number"
                className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 ${touched.DIVAS && errors.DIVAS
                  ? 'border-red-500 focus:ring-red-500'
                  : 'focus:ring-opacity-50'
                  }`}
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: touched.DIVAS && errors.DIVAS ? '#d63031' : theme.border,
                  color: theme.text,
                }}
              />
              <ErrorMessage name="DIVAS" component="div" className="text-red-500 text-sm mt-1" />
            </div>
          </div>

          <div
            className="flex justify-between items-center pt-4 mt-8 border-t sticky bottom-0 z-[100] py-4 -mx-4 md:-mx-10 lg:-mx-12 px-4 md:px-10 lg:px-12"
            style={{
              borderColor: theme.border,
              backgroundColor: theme.card,
              boxShadow: '0 -10px 20px rgba(0,0,0,0.05)'
            }}
          >
            {!isFirstStep && onBack && (
              <button
                type="button"
                onClick={onBack}
                className="px-6 py-2 rounded-full font-semibold transition-all duration-200 hover:scale-105 active:scale-95 text-sm"
                style={{
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  color: theme.text,
                  border: `1px solid ${theme.border}`
                }}
              >
                Back
              </button>
            )}
            <div className={isFirstStep ? "ml-auto" : ""}>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-2.5 rounded-full font-bold text-white transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base shadow-lg animate-flow"
                style={{
                  backgroundImage: `linear-gradient(to right, ${theme.brownTwo}, ${theme.secondary}, ${theme.primary}, ${theme.brownTwo})`,
                }}
              >
                {isLastStep ? "Submit" : "Next"}
              </button>
            </div>
          </div>
        </Form >
      )}
    </Formik >
  );
};

export default PatientTreatmentForm;

